import { KB_BYTES, MB_BYTES } from './components/DocumentUploader/constants';
import {
  SsmoStepsIdentifiers,
  SsmoStepStatus,
  SsmoTask,
  SsmoTaskStatus,
  STATUSES_INTL_ID,
  taskStatusToSubtitleMapping,
} from './constants';
import { SsmoTaskStep } from './types';
import { OnboardingProgressResponse } from '../../apis/merchant-onboarding-service/types';

export const calculateTaskProgress = (steps: SsmoTaskStep[]) => {
  const completedSteps = steps.filter(step => step.status === SsmoStepStatus.COMPLETED).length;

  const progress = (completedSteps / steps.length) * 100;

  return Math.round(progress);
};

export const getKybStatusId = (taskStatus: SsmoTaskStatus, steps: SsmoTaskStep[]) => {
  const submissionStepStatus = steps.find(step => step.stepId === SsmoStepsIdentifiers.KybDataSubmition)!.status;

  const verificationStepStatus = steps.find(step => step.stepId === SsmoStepsIdentifiers.KybDataVerification)!.status;

  if (taskStatus === SsmoTaskStatus.IN_PROGRESS) {
    // if user has submitted the data but verification from wolt is pending
    if (submissionStepStatus === SsmoStepStatus.COMPLETED && verificationStepStatus === SsmoStepStatus.OPEN) {
      return `${STATUSES_INTL_ID}.verification-submitted`;
    }

    return taskStatusToSubtitleMapping[taskStatus];
  }

  return taskStatusToSubtitleMapping[taskStatus];
};

export const getContractSigningStatusId = (taskStatus: SsmoTaskStatus, steps: SsmoTaskStep[]) => {
  const contractSigningStepStatus = steps.find(step => step.stepId === SsmoStepsIdentifiers.SignContract)!.status;

  const contractDetailsStepStatus = steps.find(step => step.stepId === SsmoStepsIdentifiers.ContractDetails)!.status;

  if (taskStatus === SsmoTaskStatus.IN_PROGRESS) {
    // if user has submitted the data but verification from wolt is pending
    if (contractDetailsStepStatus === SsmoStepStatus.COMPLETED && contractSigningStepStatus === SsmoStepStatus.OPEN) {
      return `${STATUSES_INTL_ID}.signature-needed`;
    }

    return taskStatusToSubtitleMapping[taskStatus];
  }

  return taskStatusToSubtitleMapping[taskStatus];
};

export const prefillLinkWithHttps = (link: string) => {
  const httpPrefix = 'http://';
  const httpsPrefix = 'https://';

  const userIsTypingPrefix = (prefix: string) => {
    const len = Math.min(prefix.length, link.length);
    for (let i = 0; i < len; i++) {
      if (prefix[i] !== link[i]) {
        return false;
      }
    }
    return true;
  };

  if (userIsTypingPrefix(httpPrefix) || userIsTypingPrefix(httpsPrefix)) {
    return link;
  }
  return httpsPrefix + link;
};

export const getPrettyFileSize = (sizeInBytes: number) => {
  if (sizeInBytes < MB_BYTES) {
    return Math.round(sizeInBytes / KB_BYTES) + 'KB';
  }
  return Math.round((sizeInBytes * 10) / MB_BYTES) / 10 + 'MB';
};

export const trimStringInTheMiddle = (s: string, maxLength: number = 30, placeholder = '...') => {
  const trimmedLength = maxLength - placeholder.length;
  const leftIdx = Math.round(trimmedLength / 2);
  const rightIdx = trimmedLength - leftIdx;
  return s.length > maxLength ? `${s.slice(0, leftIdx)}...${s.slice(-rightIdx)}` : s;
};

export const getDisabledTasks = (onboardingProgress: OnboardingProgressResponse): Set<SsmoTask> => {
  const disabledTasks = new Set<SsmoTask>();

  if ([SsmoTaskStatus.BLOCKED, SsmoStepStatus.COMPLETED].includes(onboardingProgress.tasks.CONTRACT_SIGNING.status)) {
    disabledTasks.add(SsmoTask.CONTRACT_SIGNING);
  }

  if ([SsmoTaskStatus.BLOCKED, SsmoTaskStatus.COMPLETED].includes(onboardingProgress.tasks.KYC.status)) {
    disabledTasks.add(SsmoTask.KYC);
  }

  if ([SsmoTaskStatus.BLOCKED, SsmoTaskStatus.COMPLETED].includes(onboardingProgress.tasks.KYB.status)) {
    disabledTasks.add(SsmoTask.KYB);
  }

  const isContractCreated =
    onboardingProgress.tasks.CONTRACT_SIGNING.steps.find(step => step.stepId === SsmoStepsIdentifiers.ContractDetails)
      ?.status === SsmoStepStatus.COMPLETED;

  if (onboardingProgress?.tasks.OPENING_TIMES.status === SsmoTaskStatus.BLOCKED || isContractCreated) {
    disabledTasks.add(SsmoTask.OPENING_TIMES);
  }

  if (onboardingProgress?.tasks.MENU_SUBMISSION.status === SsmoTaskStatus.BLOCKED || isContractCreated) {
    disabledTasks.add(SsmoTask.MENU_SUBMISSION);
  }
  return disabledTasks;
};
